import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

const StockMovements = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableColumns, setTableColumns] = useState([]);
  const [columnSettings, setColumnSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnMenu, setShowColumnMenu] = useState(null);
  const [filters, setFilters] = useState({
    productId: '',
    warehouseId: '',
    movementType: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadColumnSettings();
    loadProducts();
    loadWarehouses();
    loadMovements();
  }, [organization]);

  const loadProducts = async () => {
    try {
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await inventoryApi.getWarehouses(organization);
      const warehousesData = Array.isArray(response) ? response : (response.data || []);
      setWarehouses(warehousesData);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const loadMovements = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filterParams = {};
      if (filters.productId) filterParams.productId = filters.productId;
      if (filters.warehouseId) filterParams.warehouseId = filters.warehouseId;
      if (filters.movementType) filterParams.movementType = filters.movementType;
      if (filters.startDate) filterParams.startDate = filters.startDate;
      if (filters.endDate) filterParams.endDate = filters.endDate;
      
      const response = await inventoryApi.getStockMovements(organization, filterParams);
      const movementsData = Array.isArray(response) ? response : (response.data || []);
      setMovements(movementsData);

      // Generate dynamic columns
      if (movementsData.length > 0) {
        const saved = localStorage.getItem(`stockMovementsColumns_${organization}`);
        let savedSettings = {};
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            savedSettings = parsed.settings || {};
          } catch (e) {
            console.error('Error parsing saved settings:', e);
          }
        }

        const firstItem = movementsData[0];
        const columns = Object.keys(firstItem)
          .filter(key => key !== 'id')
          .map((key, index) => ({
            key,
            label: formatColumnLabel(key),
            visible: savedSettings[key]?.visible !== false,
            order: savedSettings[key]?.order ?? index
          }));
        columns.sort((a, b) => a.order - b.order);
        setTableColumns(columns);
      }
    } catch (err) {
      setError(err.message || 'Failed to load stock movements');
      setMovements([]);
    } finally {
      setLoading(false);
    }
  };

  // Load column settings from localStorage
  const loadColumnSettings = () => {
    const saved = localStorage.getItem(`stockMovementsColumns_${organization}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed.settings || {});
        setSortConfig(parsed.sort || { key: null, direction: 'asc' });
        return parsed.settings || {};
      } catch (e) {
        console.error('Error loading column settings:', e);
        return {};
      }
    }
    return {};
  };

  // Save column settings to localStorage
  const saveColumnSettings = (settings, sort) => {
    localStorage.setItem(`stockMovementsColumns_${organization}`, JSON.stringify({
      settings,
      sort
    }));
  };

  // Helper function to format column labels
  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key) => {
    const newSettings = {
      ...columnSettings,
      [key]: {
        ...columnSettings[key],
        visible: !(columnSettings[key]?.visible ?? true)
      }
    };
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);

    setTableColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Move column up in order
  const moveColumnUp = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex <= 0) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex - 1], newColumns[currentIndex]] =
      [newColumns[currentIndex], newColumns[currentIndex - 1]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Move column down in order
  const moveColumnDown = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex >= tableColumns.length - 1) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex], newColumns[currentIndex + 1]] =
      [newColumns[currentIndex + 1], newColumns[currentIndex]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    saveColumnSettings(columnSettings, newSort);
  };

  // Reset column settings
  const resetColumnSettings = () => {
    localStorage.removeItem(`stockMovementsColumns_${organization}`);
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
    if (movements.length > 0) {
      const firstItem = movements[0];
      const columns = Object.keys(firstItem)
        .filter(key => key !== 'id')
        .map((key, index) => ({
          key,
          label: formatColumnLabel(key),
          visible: true,
          order: index
        }));
      setTableColumns(columns);
    }
  };

  // Format cell values
  const formatCellValue = (value, key, movement) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Handle product_id
    if (key === 'product_id' || key === 'productId') {
      return getProductName(value);
    }
    
    // Handle warehouse_id
    if (key === 'warehouse_id' || key === 'warehouseId') {
      return getWarehouseName(value);
    }
    
    // Handle movement_type
    if (key === 'movement_type' || key === 'movementType') {
      return (
        <span className={`pm-movement-badge ${getMovementTypeClass(value)}`}>
          {(value || 'unknown').toUpperCase()}
        </span>
      );
    }
    
    // Handle quantity
    if (key === 'quantity') {
      const movementType = movement.movement_type || movement.movementType || 'unknown';
      const displayQuantity = movementType === 'out' ? -Math.abs(value) : value;
      return (
        <span className={displayQuantity >= 0 ? 'pm-quantity-positive' : 'pm-quantity-negative'}>
          {displayQuantity > 0 ? '+' : ''}{displayQuantity}
        </span>
      );
    }
    
    // Handle dates
    if (key.includes('date') || key.includes('Date') || key.includes('_at')) {
      return formatDate(value);
    }
    
    return String(value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    loadMovements();
  };

  const handleClearFilters = () => {
    setFilters({
      productId: '',
      warehouseId: '',
      movementType: '',
      startDate: '',
      endDate: ''
    });
    setTimeout(() => loadMovements(), 100);
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown';
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : 'Unknown';
  };

  const getMovementTypeClass = (type) => {
    const typeMap = {
      'in': 'movement-in',
      'out': 'movement-out',
      'adjustment': 'movement-adjustment',
      'transfer': 'movement-transfer',
      'production': 'movement-production',
      'return': 'movement-return'
    };
    return typeMap[type?.toLowerCase()] || 'movement-other';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading && movements.length === 0 && !products.length) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading')}
      </div>
    );
  }

  // Filter and search movements
  const filteredMovements = movements.filter(movement => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return Object.values(movement).some(value =>
      String(value).toLowerCase().includes(term)
    );
  });

  // Sort movements
  const sortedMovements = [...filteredMovements].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (aValue === bValue) return 0;
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    const comparison = aValue < bValue ? -1 : 1;
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const visibleColumns = tableColumns.filter(col => col.visible);

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.stock_movements_title')}</h2>
      </div>

      {error && (
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="pm-toolbar">
        <div className="pm-search-wrap">
          <input
            type="search"
            placeholder={t('inventory.search_movements_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-filter-wrap">
          <select
            name="productId"
            value={filters.productId}
            onChange={handleFilterChange}
          >
            <option value="">{t('inventory.all_products_option')}</option>
            {products.map(product => (
              <option key={product.id} value={product.id}>
                {product.name} ({product.sku || product.SKU || 'N/A'})
              </option>
            ))}
          </select>
          <select
            name="warehouseId"
            value={filters.warehouseId}
            onChange={handleFilterChange}
          >
            <option value="">{t('inventory.all_warehouses_option')}</option>
            {warehouses.map(warehouse => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
          <select
            name="movementType"
            value={filters.movementType}
            onChange={handleFilterChange}
          >
            <option value="">{t('inventory.all_types_option')}</option>
            <option value="in">{t('inventory.movement_type_in')}</option>
            <option value="out">{t('inventory.movement_type_out')}</option>
            <option value="adjustment">{t('inventory.movement_type_adjustment')}</option>
            <option value="transfer">{t('inventory.movement_type_transfer')}</option>
            <option value="production">{t('inventory.movement_type_production')}</option>
            <option value="return">{t('inventory.movement_type_return')}</option>
          </select>
          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
            placeholder={t('inventory.start_date_label')}
          />
          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
            placeholder={t('inventory.end_date_label')}
          />
          <button className="pm-btn-primary" onClick={handleApplyFilters}>
            {t('inventory.apply_filters_button')}
          </button>
          <button className="pm-btn-secondary" onClick={handleClearFilters}>
            {t('inventory.clear_filters_button')}
          </button>
        </div>
      </div>

      <div className="pm-summary-grid">
        <div className="pm-summary-card">
          <h4>{t('inventory.total_movements')}</h4>
          <p className="pm-summary-value">{movements.length}</p>
        </div>
        <div className="pm-summary-card">
          <h4>{t('inventory.total_in')}</h4>
          <p className="pm-summary-value">
            {movements.filter(m => (m.movement_type || m.movementType) === 'in').length}
          </p>
        </div>
        <div className="pm-summary-card">
          <h4>{t('inventory.total_out')}</h4>
          <p className="pm-summary-value">
            {movements.filter(m => (m.movement_type || m.movementType) === 'out').length}
          </p>
        </div>
        <div className="pm-summary-card">
          <h4>{t('inventory.total_adjustments')}</h4>
          <p className="pm-summary-value">
            {movements.filter(m => (m.movement_type || m.movementType) === 'adjustment').length}
          </p>
        </div>
      </div>

      <div className="pm-table-wrap">
        <table className="pm-table" role="grid">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>
                <button
                  className="pm-column-btn visibility-btn"
                  onClick={() => setShowColumnMenu(showColumnMenu === 'menu' ? null : 'menu')}
                  title={t('inventory.show_hidden_columns_title')}
                >
                  ⋮
                </button>
                {showColumnMenu === 'menu' && (
                  <div className="pm-column-menu">
                    {tableColumns.filter(col => !col.visible).map(col => (
                      <button key={col.key} onClick={() => {
                        toggleColumnVisibility(col.key);
                        setShowColumnMenu(null);
                      }}>
                        {t('inventory.show_column_button')} {col.label}
                      </button>
                    ))}
                    <button onClick={() => {
                      resetColumnSettings();
                      setShowColumnMenu(null);
                    }}>
                      {t('inventory.reset_all_button')}
                    </button>
                  </div>
                )}
              </th>
              {visibleColumns.map((col, index) => (
                <th key={col.key} className="pm-sortable-header">
                  <div className="pm-header-content">
                    <div className="pm-header-controls">
                      <button
                        className="pm-column-btn"
                        onClick={() => moveColumnUp(col.key)}
                        disabled={index === 0}
                        title={t('inventory.move_left_title')}
                      >
                        ◀
                      </button>
                      <button
                        className="pm-column-btn"
                        onClick={() => moveColumnDown(col.key)}
                        disabled={index === visibleColumns.length - 1}
                        title={t('inventory.move_right_title')}
                      >
                        ▶
                      </button>
                      <button
                        className="pm-column-btn visibility-btn"
                        onClick={() => setShowColumnMenu(showColumnMenu === col.key ? null : col.key)}
                        title={t('inventory.column_options_title')}
                      >
                        ⋮
                      </button>
                    </div>
                    <div className="pm-header-label" onClick={() => handleSort(col.key)}>
                      {col.label}
                      {sortConfig.key === col.key && (
                        <span className="pm-sort-indicator">
                          {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </div>
                  </div>
                  {showColumnMenu === col.key && (
                    <div className="pm-column-menu">
                      <button onClick={() => {
                        toggleColumnVisibility(col.key);
                        setShowColumnMenu(null);
                      }}>
                        {t('inventory.hide_column_button')}
                      </button>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleColumns.length + 1}>
                  <div className="pm-skeleton" aria-busy="true">{t('inventory.loading')}</div>
                </td>
              </tr>
            ) : sortedMovements.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + 1}>
                  <div className="pm-empty-state">
                    <div className="pm-empty-state-icon" aria-hidden="true">📈</div>
                    <h3>{t('inventory.no_stock_movements_found')}</h3>
                  </div>
                </td>
              </tr>
            ) : (
              sortedMovements.map((movement) => (
                <tr key={movement.id}>
                  <td></td>
                  {visibleColumns.map(col => (
                    <td key={col.key}>
                      {formatCellValue(movement[col.key], col.key, movement)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockMovements;
